/* global window */
import lodashMerge from 'lodash/merge';
import Fingerprint2 from 'fingerprintjs2';
import Cookie from 'js-cookie';

import {assertDependencies, isInsideIFrame} from '../lib/util';
import async from '../lib/async';

import {EMIT as SCENE_OBSERVER_EMIT} from '../scene_observer/scene_observer';
import {EMIT as VR_MODE_OBSERVER_EMIT} from '../vr_mode_observer/vr_mode_observer';
import {EMIT as IDLE_DETECTOR_EMIT} from '../idle_detector/idle_detector';

import Hotspot from '../krpano_client/models/Hotspot';
import TrackerAnnounce from './models/TrackerAnnounce';
import TrackerEvent from './models/TrackerEvent';
import TrackerActivity, {GazePoint} from './models/TrackerActivity';

const MANIFEST = require('./manifest');

const DEFAULT_OPTIONS = {
	/**
	 * Nothing will happen if audience tracker is not enabled. This should be used mostly for debugging,
	 * as audience tracker is not offering any services that can be used by others.
	 * This setting cannot be changed during runtime.
	 */
	enabled: true,

	/**
	 * General update interval. Trigger attention cycling and dispatches
	 */
	update_interval: 500,

	/**
	 * How much activity tracking to accumulate into each dispatch
	 */
	activity_duration: 1000 * 30,

	/**
	 * How many times to attempt a dispatch before discarding a record
	 */
	max_failed_dispatches: 10,

	/**
	 * How long to wait after each failed request before we try again.
	 * This is used for all types of API calls (announce, event and activity).
	 */
	retry_delays: [
		1000 * 5,
		1000 * 10,
		1000 * 15,
		1000 * 30,
		1000 * 45,
		1000 * 60,
		1000 * 60 * 1.5,
		1000 * 60 * 3,
		1000 * 60 * 5,
		1000 * 60 * 10
	],

	/**
	 * Track events and activity even while user is marked as "idle".
	 * This should probably be false, otherwise the session on server would never end.
	 * Also, we don't want to catch scripted events, only those triggered by the user.
	 */
	track_while_idle: false,

	/**
	 * Whether we should track attention (hotspots)
	 */
	track_attention: true,

	/**
	 * How often do we refresh hotspot attentions
	 */
	attention_interval_ms: 350,

	/**
	 * Track the center of viewport. Useful for tours that need advanced analytics.
	 */
	track_gaze: true,

	/**
	 * How often do we gather and save gaze data
	 */
	gaze_interval_ms: 150,

	/**
	 * In case queues are clogged up and we need to flush, how many top records do we take.
	 */
	flush_limit: 50,

	// Supplied by server
	site_id: null,
	site_version_id: null,
	site_key: null,

	// Custom session configuration
	custom_tag: null,
	custom_data: null
};

function AudienceTracker(options = DEFAULT_OPTIONS, deps = MANIFEST.dependencies) {
	const thisAttentionAccumulator = this;

	options = lodashMerge({}, DEFAULT_OPTIONS, options);
	assertDependencies(MANIFEST.name, MANIFEST.dependencies, deps);

	const log = deps.logger.child(MANIFEST.name);

	let _instanceId = null;
	let _updateInterval = null;
	let _ended = false;

	let _failedInits = 0;
	let _lastFailedInitAt = null;

	let _userActiveSince = null;

	let _dispatchLock = 0;
	const _activityDispatchQueue = [];
	_activityDispatchQueue.name = 'Activity dispatch queue';

	const _eventDispatchQueue = [];
	_eventDispatchQueue.name = 'Event dispatch queue';

	/**
	 * @type {TrackerActivity}
	 */
	let _currentActivity = null;

	Object.assign(thisAttentionAccumulator, /** @lends AudienceTracker.prototype */ {
		init,
		isInitialized: () => !!_instanceId,
		getInstanceId: () => _instanceId,
		getActivityDispatchQueue: () => _activityDispatchQueue,
		getEventDispatchQueue: () => _eventDispatchQueue,
		sendCustomEvent,
		endSession,
	});

	function sendCustomEvent(customType, target = null, value = null, customData = null) {
		const evt = makeEvent(TrackerEvent.TYPES.custom);
		evt.custom_event_type = customType;
		evt.target = target;
		evt.value = value;
		evt.additional_data = customData;
		sendEvent(evt);
	}

	function endSession() {
		sendEvent(makeEvent(TrackerEvent.TYPES.end_session));
		endTracking();
	}

	function init(callback) {
		clearInterval(_updateInterval);

		if (!options.enabled) {
			log.warn(`Disabled through config parameter!`);
			return callback && callback();
		}

		deps.jQuery(deps.window).on('beforeunload', handleExit);

		return initInstance(err => {
			_updateInterval = setInterval(update, options.update_interval);

			if (err) {
				_failedInits++;
				_lastFailedInitAt = new Date();
				return callback && callback(err);
			}

			if (!deps.idleDetector.isIdle()) {
				_userActiveSince = new Date();
				deps.idleDetector.on(IDLE_DETECTOR_EMIT.idle, handleIdleChanged);
			}

			deps.sceneObserver.on(SCENE_OBSERVER_EMIT.entered_scene, handleSceneChange);
			// Emit initial scene
			handleSceneChange(deps.krPanoClient.getSceneInfo(), null);

			deps.vrModeObserver.on(VR_MODE_OBSERVER_EMIT.vr_mode_change, handleVrModeChange);

			if (options.track_attention) {
				setInterval(updateAttention, options.attention_interval_ms);
			}

			if (options.track_gaze) {
				setInterval(updateGaze, options.gaze_interval_ms);
			}


			log.verbose('Initialized');

			return callback && callback(null);
		});
	}

	function initInstance(callback) {
		const job = async();

		Fingerprint2.get(job.callback('fingerprint').bind(job, null));

		if (deps.userProvider) {
			log.verbose(`Using user provider "${deps.userProvider.getProviderName()}"`);
			deps.userProvider.getUserId(job.callback('userId'));
		}

		if (deps.customDataProvider) {
			deps.customDataProvider.getCustomTag(job.callback('customTag'));
			deps.customDataProvider.getCustomData(job.callback('customData'));
		}

		return job.wait((err, data) => {
			if (err) {
				return callback(err);
			}

			const values = data.fingerprint.map(c => c.value);
			const fingerprint = Fingerprint2.x64hash128(values.join(''), 31);
			log.verbose(`Browser fingerprint loaded: ${fingerprint}`);

			Cookie.set('hw_id', fingerprint);
			if (Cookie.get('site_id') != options.site_id) { // eslint-disable-line eqeqeq
				Cookie.set('site_id', options.site_id, {path: window.location.pathname});
				Cookie.set('site_key', window.location.pathname, {path: window.location.pathname});
			}

			const msg = new TrackerAnnounce();
			msg.client_version = deps.krPanoClient.getVersion();
			msg.device_fingerprint = fingerprint;
			if (data.userId) {
				msg.user_id = data.userId;
				msg.user_id_provider = deps.userProvider.getProviderName();
			}
			msg.site_id = options.site_id;
			msg.site_version_id = options.site_version_id;
			msg.site_key = options.site_key;
			msg.iframe = isInsideIFrame(deps.window);
			msg.referrer = deps.document.referrer;
			if (data.customTag) {
				msg.custom_tag = data.customTag;
			}
			if (data.customData) {
				msg.custom_data = data.customData;
			}

			return deps.trackerApi.announce(msg, (err, result) => {
				if (err) {
					return callback(err);
				}

				_instanceId = result.instance_id;

				log.verbose(`Obtained instance id: "${_instanceId}"`);

				return callback(null);
			});
		});
	}

	function update() {
		const now = new Date();

		if (!_instanceId) {
			// The init has failed. Try again, if we can.
			const coolOffPeriod = options.retry_delays[_failedInits - 1] || options.retry_delays[options.retry_delays.length - 1];
			if (coolOffPeriod && now - _lastFailedInitAt > coolOffPeriod) {
				// We can. Try again.
				init();
			}
			return;
		}

		if (_currentActivity && now - _currentActivity.started_at > options.activity_duration) {
			nextActivity();
		}

		dispatch(false);
	}

	function updateAttention() {
		if (!_currentActivity || deps.idleDetector.isIdle()) {
			return;
		}

		const hotspots = deps.krPanoClient.getHotspots();

		hotspots.forEach(hotspot => {
			if (!hotspot.id) {
				// We only track marked hotspots
				return;
			}

			if (hotspot.scene !== _currentActivity.location) {
				// Looks like we haven't caught up with location change yet. We can discard this hotspot
				return;
			}

			const hash = hotspot.location === Hotspot.LOCATIONS.in_focus
				? (_currentActivity.focused_attention || (_currentActivity.focused_attention = {}))
				: hotspot.location === Hotspot.LOCATIONS.on_edge
					? (_currentActivity.peripheral_attention || (_currentActivity.peripheral_attention = {}))
					: null;
			if (!hash) {
				return;
			}

			hash[hotspot.id] = (hash[hotspot.id] || 0) + options.attention_interval_ms;
		});
	}

	function updateGaze() {
		if (!_currentActivity || deps.idleDetector.isIdle() || _ended) {
			return;
		}

		const gaze = deps.krPanoClient.getGazeInfo();
		const mouse = deps.krPanoClient.getMouseInfo();

		const gazePoint = new GazePoint();
		gazePoint.s_x = gaze.x;
		gazePoint.s_y = gaze.y;
		gazePoint.fov = gaze.fov;
		gazePoint.p_x = mouse.sphere_x;
		gazePoint.p_y = mouse.sphere_y;
		_currentActivity.gaze_points.push(gazePoint);
	}

	function handleIdleChanged(isIdle) {
		const now = new Date();
		if (isIdle) {
			// Going from active to idle. Add up active ms to the current activity record
			if (_currentActivity) {
				accumulateUserActiveMs(_currentActivity, now);
			}
			_userActiveSince = null;
		} else {
			// Going from idle to active. Remember this timestamp as the start of current active span
			_userActiveSince = now;
		}
	}

	function accumulateUserActiveMs(activity, to) {
		const from = Math.max(_userActiveSince, activity.started_at);
		activity.user_active_ms += (to - from);
	}

	/**
	 * @param {SceneInfo} current
	 * @param {SceneInfo} prev
	 */
	function handleSceneChange(current, prev) {
		if ((!options.track_while_idle && deps.idleDetector.isIdle()) || _ended) {
			return;
		}

		const step = deps.sceneObserver.getJourneyStep();

		if (prev) {
			const evt = makeEvent(TrackerEvent.TYPES.location_exit);
			evt.location = prev.name;
			evt.journey_step = step - 1;
			sendEvent(evt);
		}

		if (current) {
			const evt = makeEvent(TrackerEvent.TYPES.location_enter);
			evt.location = current.name;
			evt.journey_step = step;
			sendEvent(evt);

			nextActivity();
		}
	}

	function handleVrModeChange(isInVrMode, initialEmit) {
		if (initialEmit) {
			return;
		}

		let evt;
		if (isInVrMode) {
			evt = makeEvent(TrackerEvent.TYPES.vr_mode_enter);
		} else {
			evt = makeEvent(TrackerEvent.TYPES.vr_mode_exit);
		}

		sendEvent(evt);

		// Create a new activity, with this active VR mode
		nextActivity();
	}

	/**
	 * Called when browser is about to be closed. Flush everything we have queued at once
	 */
	function handleExit() {
		sendEvent(makeEvent(TrackerEvent.TYPES.exit));
		endSession();
		
		// Added this to delay tab closing so that the API call can be sent. Browsers recently introduced changed so that XHR calls are cut off after closing.
		setTimeout(function() {  }, 7000);
		return undefined;
	}

	/**
	 * @param type
	 * @return {TrackerEvent}
	 */
	function makeEvent(type) {
		const evt = new TrackerEvent();
		evt.event_type = type;
		evt.location = deps.sceneObserver.getCurrentSceneName();
		evt.journey_step = deps.sceneObserver.getJourneyStep();
		evt.in_vr_mode = deps.krPanoClient.isInVrMode();
		evt.is_user_triggered = !deps.idleDetector.isIdle();
		evt.is_window_focused = true;
		evt.timestamp_ago_ms = 0;
		return evt;
	}

	/**
	 * @param {TrackerEvent} evt
	 */
	function sendEvent(evt) {
		log.verbose(`EVENT ${evt.event_type} (location: ${evt.location}, target: ${evt.target}, value: ${evt.value})`);
		if (!_instanceId || _ended) {
			return;
		}

		_eventDispatchQueue.push(evt);
		dispatch();
	}

	function nextActivity() {
		if (_ended) {
			return;
		}

		const now = new Date();

		if (_currentActivity) {
			_currentActivity.ended_at = now;
			if (!deps.idleDetector.isIdle()) {
				accumulateUserActiveMs(_currentActivity, now);
			}

			if (_currentActivity.user_active_ms <= 0) {
				log.verbose(`Discarding activity ${_currentActivity.toString()}, as user hasn't been active during its span`);
			} else {
				log.verbose(`Activity ${_currentActivity.toString()} has been closed and enqueued for dispatch`);
				_activityDispatchQueue.push(_currentActivity);
			}
		}

		_currentActivity = new TrackerActivity();
		_currentActivity.instance_id = _instanceId;
		_currentActivity.location = deps.sceneObserver.getCurrentSceneName();
		_currentActivity.journey_step = deps.sceneObserver.getJourneyStep();
		_currentActivity.in_vr_mode = deps.krPanoClient.isInVrMode();
		_currentActivity.user_active_ms = 0;
		_currentActivity.window_focused_ms = 0;
		_currentActivity.focused_attention = null;
		_currentActivity.peripheral_attention = null;
		_currentActivity.background_attention = null;
		_currentActivity.gaze_points = [];
		_currentActivity.gaze_interval_ms = options.gaze_interval_ms;
		_currentActivity.started_at = now;
		log.verbose(`Started accumulating activity ${_currentActivity}`);
	}

	/**
	 * Empty all the queues by sending the records to API.
	 */
	function dispatch() {
		if (_ended) {
			return;
		}

		_dispatchLock++;
		if (_dispatchLock > 1) {
			return;
		}

		const job = async();

		dispatchQueue(_activityDispatchQueue, deps.trackerApi.sendActivity, job.callback());
		dispatchQueue(_eventDispatchQueue, deps.trackerApi.sendEvent, job.callback());

		return job.wait(() => {
			const needAnotherDispatch = _dispatchLock > 1;
			_dispatchLock = 0;
			if (needAnotherDispatch) {
				dispatch();
			}
		});
	}

	/**
	 * Returns first record that meets conditions for dispatch (timers and such)
	 * @param {DispatchablePayload[]} queue
	 * @param flush If flushing, all limits and timers are ignored
	 */
	function takeRecordForDispatch(queue, flush = false) {
		const now = new Date();

		for (let i = 0; i < queue.length; i++) {
			const dp = queue[0];
			if (!dp.failed_dispatches || flush) {
				return queue.splice(i, 1)[0];
			}

			const dispatchCoolOffPeriod = options.retry_delays[dp.failed_dispatches - 1];
			if (now - dp.failed_dispatch_at > dispatchCoolOffPeriod) {
				return queue.splice(i, 1)[0];
			}
		}

		return null;
	}

	function dispatchQueue(queue, dispatchFn, callback) {
		if (_ended) {
			return;
		}

		/** @type {DispatchablePayload} */
		const dp = takeRecordForDispatch(queue, false);
		if (!dp) {
			// Nothing to dispatch (any more)
			return callback(null);
		}

		const now = new Date();
		dp.prepareForDispatch(now);

		return dispatchFn(_instanceId, dp, err => {
			if (err) {
				dp.failed_dispatches++;
				dp.failed_dispatch_at = now;
				if (dp.failed_dispatches - 1 >= options.retry_delays.length) {
					// No more retries. Drop this payload
					log.error(`${dp.toString()} has failed dispatch ${dp.failed_dispatches} times. It will be discarded`, dp);
				} else {
					queue.push(dp);
					log.warn(`${dp.toString()} has failed to be dispatched ${dp.failed_dispatches} times. `
						+ `Next attempt will be in ${options.retry_delays[dp.failed_dispatches - 1]} ms`);
				}
			} else {
				log.verbose(`${dp.toString()} has been dispatched`);
			}

			return dispatchQueue(queue, dispatchFn, callback);
		});
	}

	function endTracking() {
		if (_ended) {
			return;
		}

		_ended = true;

		log.info('Tracking ended. Flushing all events and activities...');

		flushQueue(_activityDispatchQueue, deps.trackerApi.sendActivity);
		flushQueue(_eventDispatchQueue, deps.trackerApi.sendEvent);
	}

	/**
	 * Send all remaining events and activity records at once, synchronously.
	 * This should be called when the window might go away at any moment (eg. user is exiting the tour)
	 */
	function flushQueue(queue, dispatchFn) {
		if (!_instanceId) {
			return;
		}

		log.info(`Flushing ${queue.name}`);

		const now = new Date();
		const payload = queue.splice(0, options.flush_limit);

		payload.forEach(dp => {
			dp.prepareForDispatch(now);
		});

		return dispatchFn(_instanceId, payload, err => {
			if (err) {
				// We'll probably be gone before this happens, but just in case
				log.error(err);
			}
		});
	}
}

// *********************************************************************************************************************

export default AudienceTracker;
